import { createContext, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import CompanyManager from '../services/api/CompanyManager';
import useAuth from '../hooks/global/useAuth';
import { CouponDetailsInterface } from '../services/exports/Interfaces';
import useStore from '../hooks/store/useStore';
import { COUPON_TYPES, PROMO_CODE_TYPES, REDUX_STORE_SLICES } from '../services/exports/Constants';
import { useTranslation } from 'react-i18next';
import { ReservedHttpResponseCodes } from '../services/exports/Types';
import MultiPromoCodeModal from '../modals/MultiPromoCodeModal';
import { actionCreators } from '../store/actions';
import ReduxActions from '../store/ReduxActions';
import useAlert from '../hooks/utility/useAlert';

interface Props {
  children: JSX.Element;
}

interface IPromoCodeContext {
  promoCodeInput: string;
  appliedPromoCode: string | null;
  showLoginPrompt: boolean;
  isLoading: boolean;
  setPromoCode: (promoCode: CouponDetailsInterface) => void;
  setPromoCodeInput: (promoCodeInput: string) => void;
  removePromoCode: () => void;
  applyPromoCode: (code?: string) => Promise<any>;
}

export const PromoCodeContext = createContext<IPromoCodeContext>(null);

const PromoCodeProvider = ({ children }: Props) => {
  const { isLoggedIn } = useAuth();
  const { company } = useStore<REDUX_STORE_SLICES.INITIAL_DATA>(REDUX_STORE_SLICES.INITIAL_DATA);
  const { cached_order } = useStore<REDUX_STORE_SLICES.ORDER>(REDUX_STORE_SLICES.ORDER);
  const { profile } = useStore<REDUX_STORE_SLICES.PROFILE>(REDUX_STORE_SLICES.PROFILE);
  const { t } = useTranslation(undefined, {
    keyPrefix: 'Providers:PromoCodeProvider',
  });
  const alert = useAlert();
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [promoCodeInput, setPromoCodeInput] = useState<string>(cached_order?.coupon_details?.code ?? '');
  const [appliedPromoCode, setAppliedPromoCode] = useState<string | null>(cached_order?.coupon_details?.code);
  const [showMultiPromoCodeModal, setShowMultiPromoCodeModal] = useState(false);

  useEffect(() => {
    if (cached_order?.coupon_type !== COUPON_TYPES.PROMO_CODE && appliedPromoCode) {
      setPromoCodeInput('');
      setAppliedPromoCode(null);
    }
  }, [cached_order?.coupon_type, cached_order?.coupon_details?.code]);

  const actualizePromoCode = async (): Promise<void> => {
    setIsLoading(true);
    const { response, success } = await CompanyManager.getPromoCodeDetails(
      company?.id,
      cached_order.coupon_details.code,
    );
    setIsLoading(false);

    if (!success) {
      removePromoCode();

      return;
    }

    const cachedComponent = cached_order.coupon_details.components.find((component) => !component.is_used);
    const actualComponent = response.data.data.components.find((component) => !component.is_used);

    if (cachedComponent.id !== actualComponent.id) {
      setPromoCode(response.data.data);
    }
  };

  useEffect(() => {
    if (cached_order?.coupon_details?.code && profile?.id) {
      actualizePromoCode();
    }
  }, []);

  const applyPromoCode = async (code?: string): Promise<any> => {
    if (!isLoggedIn) {
      return setShowLoginPrompt(true);
    }

    setIsLoading(true);
    const { response, success } = await CompanyManager.getPromoCodeDetails(company?.id, code ?? promoCodeInput);
    setIsLoading(false);

    if (!success) {
      applyPromoErrorHandler(response);

      return;
    }

    setPromoCode(response.data.data);
    toast.success(t('feedback.promo_code_added'));
  };

  function applyPromoErrorHandler(error: any): void {
    if (error?.response?.status === ReservedHttpResponseCodes.ErrorResponseAlertWindow) {
      alert.error({
        title: error?.response?.data?.title,
        description: error?.response?.data?.description,
      });
    }

    removePromoCode();
  }

  const removePromoCode = (): void => {
    setPromoCodeInput('');

    ReduxActions.dispatch(
      actionCreators.order.updateOrder({
        coupon_type: null,
        coupon_details: null,
      }),
    );

    appliedPromoCode && toast.error(t('feedback.promo_code_removed', { code: appliedPromoCode }));

    setAppliedPromoCode(null);
  };

  const setPromoCode = (promoCode: CouponDetailsInterface): void => {
    if (!isLoggedIn) {
      return setShowLoginPrompt(true);
    }

    ReduxActions.dispatch(
      actionCreators.order.updateOrder({
        coupon_type: COUPON_TYPES.PROMO_CODE,
        coupon_details: promoCode,
      }),
    );

    setPromoCodeInput(promoCode.code);
    setAppliedPromoCode(promoCode.code);

    if (promoCode.type === PROMO_CODE_TYPES.MULTI_PROMO_CODE) {
      toggleMultiPromoCodeModal();
    }
  };

  const toggleMultiPromoCodeModal = (): void => setShowMultiPromoCodeModal(!showMultiPromoCodeModal);

  const renderMultiPromoCodeModal = useMemo(() => {
    return <MultiPromoCodeModal openModal={showMultiPromoCodeModal} toggleModal={toggleMultiPromoCodeModal} />;
  }, [showMultiPromoCodeModal]);

  const context = useMemo(
    () => ({
      promoCodeInput,
      appliedPromoCode,
      showLoginPrompt,
      isLoading,
      setPromoCode,
      setPromoCodeInput,
      removePromoCode,
      applyPromoCode,
    }),
    [promoCodeInput, appliedPromoCode, showLoginPrompt, isLoading, isLoggedIn],
  );

  return (
    <PromoCodeContext.Provider value={context}>
      {children}
      {renderMultiPromoCodeModal}
    </PromoCodeContext.Provider>
  );
};

export default PromoCodeProvider;
